import { type ReactNode } from 'react';
import { ArtifactWorkspace, Icon, icons, theme } from '@agentisai/design-system';
import { formatArtifactSize, formatArtifactType } from '../lib/formatArtifactMeta';
import { ApiError } from '../queries/client';
import { useArtifacts } from '../queries/useArtifacts';
import { useGetArtifact } from '../queries/useGetArtifact';
import { ArtifactPreview, previewableArtifact } from './ArtifactPreview';

const muted = {
  padding: theme.space[5],
  margin: 0,
  color: theme.color.neutral.muted,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

const downloadLink = {
  color: theme.color.accent.blue[700],
  font: 'inherit',
} as const;

export interface FileViewPanelProps {
  /** The workstream the files belong to — scopes the reads (the BFF is the tenant gate). */
  readonly workstreamId: string;
  /** The open file tabs (opaque ids), in tab order (the `?files=` selection). */
  readonly openFileIds: readonly string[];
  /** The active tab (the `?file=` selection) — always one of {@link openFileIds}. */
  readonly activeFileId: string;
  /** Activate a tab (click). */
  readonly onSelect: (artifactId: string) => void;
  /** Close a tab (the ×). */
  readonly onClose: (artifactId: string) => void;
  /** Fallback tab labels keyed by artifactId (the workstream ledger), for a
   *  just-produced deliverable not yet in the artifacts list. The host owns
   *  reconciling gone files out of {@link openFileIds}, so this panel only
   *  RENDERS the tabs it is given — no self-heal round-trip. */
  readonly labels?: ReadonlyMap<string, string>;
}

/**
 * FileViewPanel — the S11 View flow, multi-tab. Each file the user Views opens as a
 * tab in the centre `ArtifactWorkspace` (the same chrome agent deliverables use);
 * the active tab renders via the existing {@link ArtifactPreview} — text-family files
 * inline (markdown / code / HTML in a sandboxed iframe), a binary as a note + a
 * Download to its freshly-presigned `url`.
 *
 * Tab labels come from the S09 list (`useArtifacts`, cached + shared with the rail),
 * with a ledger fallback ({@link labels}) for a just-produced deliverable; the active
 * tab's `content`/`url` come from `getArtifact` (re-presigned on demand, ledger URLs
 * expire ~1 h). This panel only RENDERS the tabs it is handed — the host reconciles a
 * deleted / evicted file out of `openFileIds` against the artifacts list, so a 404 on
 * the active tab renders blank (not an error) only for the beat before that lands.
 */
export function FileViewPanel({
  workstreamId,
  openFileIds,
  activeFileId,
  onSelect,
  onClose,
  labels,
}: FileViewPanelProps) {
  const list = useArtifacts(workstreamId);
  const content = useGetArtifact(workstreamId, activeFileId);
  const data = content.data?.data;
  const activeGone =
    content.isError && content.error instanceof ApiError && content.error.status === 404;

  // Tab labels come from the list (`useArtifacts`) with a ledger fallback for a
  // just-produced deliverable not yet listed. The host reconciles gone files out
  // of `openFileIds`, so this panel just renders the tabs it is given.
  const listArtifacts = list.data?.data.artifacts;
  const byId = new Map((listArtifacts ?? []).map((a) => [a.artifactId, a]));
  const tabs = openFileIds.map((id) => ({
    id,
    label: byId.get(id)?.filename ?? labels?.get(id) ?? 'Loading…',
    icon: <Icon as={icons.FileText} size={14} />,
  }));

  // Body — the ACTIVE tab's content.
  let body: ReactNode;
  let metaLeft: ReactNode = null;
  let metaRight: ReactNode = null;
  if (content.isPending) {
    body = <p style={muted}>Loading preview…</p>;
  } else if (activeGone) {
    // Deleted / stale — the host drops this tab from the open set once the list
    // refetch reconciles it gone; render nothing (not an error) meanwhile.
    body = null;
  } else if (content.isError || !data) {
    body = <p style={muted}>Couldn&rsquo;t open this file. Please try again.</p>;
  } else {
    metaLeft = (
      <>
        {formatArtifactType(data.contentType)} · {formatArtifactSize(data.sizeBytes)}
      </>
    );
    metaRight = data.url ? (
      <a href={data.url} download={data.filename} style={downloadLink}>
        Download
      </a>
    ) : null;
    const previewArtifact = previewableArtifact(data);
    body = previewArtifact ? (
      <ArtifactPreview artifact={previewArtifact} />
    ) : (
      <p style={muted}>
        No inline preview for this file type{data.url ? ' — use Download to open it.' : '.'}
      </p>
    );
  }

  return (
    <ArtifactWorkspace
      tabs={tabs}
      activeId={activeFileId}
      onSelect={onSelect}
      onClose={onClose}
      metaLeft={metaLeft}
      metaRight={metaRight}
    >
      {body}
    </ArtifactWorkspace>
  );
}
FileViewPanel.displayName = 'FileViewPanel';
